// @ts-check

import { endpoints } from "@/utils/common/requests/endpoints";
import { useFetch } from "../useFetch";
import { genericQuery } from "../utils/genericQuery";

/**
 * @template T
 */
class Query {
	/** @type {string[]} */
	key = [];

	/** @type {(args?: T) => Promise<import("../utils/genericQuery").GenericQueryResponse>} */
	#fnc = null;

	/**
	 * @param {{key: string[], fnc: (args?: T) => Promise<import("../utils/genericQuery").GenericQueryResponse>}} params
	 */
	constructor({ key, fnc }) {
		this.key = key;
		this.#fnc = fnc;
	}

	/**
	 * @param {{
	 *   args?: T,
	 *   keys?: "auto" | string[],
	 *   enabled?: boolean
	 * }} options
	 * @returns {import("../useFetch").UseFetchReturn}
	 */
	fetchHook({ args = /** @type {T} */ ({}), keys = "auto", enabled = false }) {
		if (keys === "auto") {
			keys = Object.values(args).map((_key) => _key?.toString());
		}

		const key = [...this.key, ...keys];

		const fnc = async () => {
			return await this.#fnc(args);
		};

		return useFetch({
			queryKey: key,
			queryFnc: fnc,
			enabled: enabled,
		});
	}
}

export const crewQueries = {
	currentCrew: {
		key: ["crew", "current"],
		fnc: async () => {
			return await genericQuery({
				url: endpoints.crew.GET_CURRENT(),
			});
		},
	},
	getMembers: {
		key: ["crew", "members"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.GET_MEMBERS(args.id),
			});
		},
	},
	getInvitation: {
		key: ["crew", "invitation"],
		/**
		 * @param {{ invitationId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.GET_INVITATION(args.invitationId),
			});
		},
	},
	acceptInvitation: {
		key: ["crew", "invitation", "accept"],
		/**
		 * @param {{ data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.ACCEPT_INVITATION(),
				method: "post",
				payload: args.data,
			});
		},
	},
	create: {
		key: ["crew", "create"],
		/**
		 * @param {{ data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.CREATE(),
				method: "post",
				payload: args.data,
			});
		},
	},
	updateName: {
		key: ["crew", "update", "name"],
		/**
		 * @param {{ id: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.UPDATE_NAME(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	updateMemberRole: {
		key: ["crew", "update", "member", "role"],
		/**
		 * @param {{ id: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.UPDATE_MEMBER_ROLE(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	deleteMember: {
		key: ["crew", "delete", "member"],
		/**
		 * @param {{ crewId: string, userId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.DELETE_MEMBER(args),
				method: "delete",
			});
		},
	},
	renewInvitation: {
		key: ["crew", "invitation", "renew"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.RENEW_INVITATION(args.id),
				method: "post",
			});
		},
	},
	delete: {
		key: ["crew", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.crew.DELETE(args.id),
				method: "delete",
			});
		},
	},
};

export const listQueries = {
	getAll: {
		key: ["list", "all"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.GET_ALL(args.crewId),
			});
		},
	},
	create: {
		key: ["list", "create"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.CREATE(args.crewId),
				method: "post",
			});
		},
	},
	generate: {
		key: ["list", "generate"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.GENERATE(args.crewId),
				method: "post",
			});
		},
	},
	delete: {
		key: ["list", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.DELETE(args.id),
				method: "delete",
			});
		},
	},
	clear: {
		key: ["list", "clear"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemList.CLEAR(args.crewId),
				method: "delete",
			});
		},
	},
};

export const categoryQueries = {
	getAll: {
		key: ["category", "all"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.GET_ALL(args.crewId),
			});
		},
	},
	create: {
		key: ["category", "create"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.CREATE(args.crewId),
				method: "post",
			});
		},
	},
	update: {
		key: ["category", "update"],
		/**
		 * @param {{ id: string, data: Record<string, unknown> }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.UPDATE(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	delete: {
		key: ["category", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.DELETE(args.id),
				method: "delete",
			});
		},
	},
};

export const itemQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["houseItem", "getAll"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseItem.GET_ALL(args.crewId),
			});
		},
	}),
	create: {
		key: ["houseItem", "create"],
		/**
		 * @param {{ crewId: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItem.CREATE(args.crewId),
				method: "post",
			});
		},
	},
	update: {
		key: ["houseItem", "update"],
		/**
		 * @param {{ crewId: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItem.UPDATE(),
				method: "put",
				payload: args.data,
			});
		},
	},
	delete: {
		key: ["houseItem", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItem.DELETE(args.id),
				method: "delete",
			});
		},
	},
	updateBuy: {
		key: ["houseItem", "updateBuy"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItem.UPDATE_BUY(args.id),
				method: "put",
			});
		},
	},

	/** @type {Query<{crewId: string}>} */
	getNext: new Query({
		key: ["houseItem", "next"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseItem.GET_NEXT(args.crewId),
			});
		},
	}),
};

export const itemCategoriesQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["houseItemCategory", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseItemCategory.GET_ALL(args.crewId),
			});
		},
	}),
	create: {
		key: ["houseItemCategory", "create"],
		/**
		 * @param {{ crewId: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.CREATE(args.crewId),
				method: "post",
				payload: args.data,
			});
		},
	},
	update: {
		key: ["houseItemCategory", "update"],
		/**
		 * @param {{ id: string, data: Object }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.UPDATE(args.id),
				method: "put",
				payload: args.data,
			});
		},
	},
	delete: {
		key: ["houseItemCategory", "delete"],
		/**
		 * @param {{ id: string }} args
		 */
		fnc: async (args) => {
			return await genericQuery({
				url: endpoints.houseItemCategory.DELETE(args.id),
				method: "delete",
			});
		},
	},
};

export const houseTaskQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["houseTask", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.houseTask.GET_ALL(args.crewId),
			});
		},
	}),
};

export const crewMembersQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["crewMembers", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.crew.GET_MEMBERS(args.crewId),
			});
		},
	}),
};

export const economosListQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["economosList", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosList.GET_ALL(args.crewId),
			});
		},
	}),
};

export const economosMovementQueries = {
	/** @type {Query<{listId: string, year: number, month: number}>} */
	getMonth: new Query({
		key: ["economosMovement", "getMonth"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMovement.GET_MONTH({
					listId: args.listId,
					year: args.year,
					month: args.month,
				}),
			});
		},
	}),

	/** @type {Query<{listId: string, year: number, month: number}>} */
	getWithFilter: new Query({
		key: ["economosMovement", "getWithFilter"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMovement.GET_WITH_FILTER({
					listId: args.listId,
					year: args.year,
					month: args.month,
				}),
			});
		},
	}),
	/** @type {Query<{listId: string}>} */
	getTags: new Query({
		key: ["economosMovement", "getTags"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMovement.GET_TAGS({
					listId: args.listId,
				}),
			});
		},
	}),
	/** @type {Query<{list: string, dateFrom: string, dateTo: string, tags: string[], name?: string}>} */
	search: new Query({
		key: ["economosMovement", "search"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.economosMovement.SEARCH({
					list: args.list,
					dateFrom: args.dateFrom,
					dateTo: args.dateTo,
					tags: args.tags || [],
					name: args.name,
				}),
			});
		},
	}),
};

export const settingsQueries = {
	/** @type {Query<{}>} */
	get: new Query({
		key: ["settings", "get"],
		fnc: () => {
			return genericQuery({
				url: endpoints.settings.GET(),
			});
		},
	}),
};

export const splitExpenseGroupQueries = {
	/** @type {Query<{crewId: string}>} */
	getAll: new Query({
		key: ["splitExpenseGroup", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpenseGroup.GET_ALL(args.crewId),
			});
		},
	}),
	/** @type {Query<{shortId: string}>} */
	getByShortId: new Query({
		key: ["splitExpenseGroup", "shortId"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpenseGroup.GET_BY_SHORT_ID(args.shortId),
			});
		},
	}),
	/** @type {Query<{groupId: string}>} */
	getById: new Query({
		key: ["splitExpenseGroup", "id"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpenseGroup.GET_BY_ID(args.groupId),
			});
		},
	}),
};

export const splitExpenseQueries = {
	/** @type {Query<{groupId: string}>} */
	getAll: new Query({
		key: ["splitExpense", "all"],
		fnc: (args) => {
			return genericQuery({
				url: endpoints.splitExpense.GET_ALL(args.groupId),
			});
		},
	}),
};
